// Single source of truth for all KanPlan navigation modules.
//
// Each entry maps to one route in App.js. The list is the union of all
// modules previously rendered across the three legacy menu variants
// (A/B/C in Layout.legacy.js) plus admin-only items. If you add a new
// route in App.js you should also add it here so it appears in the
// sidebar and is searchable.
//
// Field reference:
//   to            — React Router path
//   label         — Display name in Swedish (UTF-8, never escaped)
//   category      — One of NAV_CATEGORIES.key in navCategories.js
//   moduleKey     — Tenant module gating key (matches backend module_access).
//                   Poster UTAN moduleKey doljs (fail-closed sedan 2026-08-12)
//                   om de inte har alwaysVisible: true eller ar admin-only.
//   alwaysVisible — true → visas for alla inloggade utan modulnyckel.
//                   Anvands ENDAST for plattformens grundvyer (Start, Inkorg).
//   badge         — Valfritt märke i menyn, t.ex. 'AI'. Statusmärken (ALFA, BETA, WIP, V1, S1,
//                   MVP, NY, DEV) visas inte längre och sätts inte (Mikael 2026-10-26).
//   iconName      — lucide-react icon component name (resolved at render time)
//   tags          — Search keywords used by useGlobalSearch (Fas 3)
//   adminOnly     — true → only visible to global admins/systemadmins
//   systemAdminOnly — true → personligt verktyg, ENDAST rollen systemadmin (strikt,
//                   döljs under simulering). Se utils/systemadminVy.js.
//   tenantAdminOnly — true → only visible to tenant_admins
//   ai            — true → KanAI-flavoured (purple accent allowed)
//   searchOnly    — true → bara i snabbsöket, aldrig i sidomenyn. `to` får då innehålla en
//                   frågeparameter som öppnar en panel (t.ex. Årshjul, Mikael F31/F42).

export const MODULES = [
  // ─── Arbetsyta ────────────────────────────────────────────────────
  // alwaysVisible: de tva enda avsiktligt nyckellosa vyerna. Sedan
  // 2026-08-12 ar sidomenyns default fail-closed — en modul utan moduleKey
  // doljs om den inte uttryckligen markerats har (eller ar admin-only).
  // Start och Inkorg ar plattformens grundvyer som varje inloggad ska na.
  { to: '/home/overview',       label: 'Start',                category: 'workspace',  iconName: 'Home',
    alwaysVisible: true,
    tags: ['hem', 'start', 'översikt', 'dashboard'] },
  { to: '/inbox',               label: 'Inkorg',               category: 'workspace',  iconName: 'Inbox',
    alwaysVisible: true,
    tags: ['inkorg', 'meddelanden', 'notiser', 'kommunikation'] },
  // Instruktioner (2026-10-13): alwaysVisible eftersom sidan i sig är en
  // plattformsyta — innehållet filtreras per modulbehörighet i backend
  // (routers/instruktioner.py). En användare utan moduler ser en tom lista.
  { to: '/instruktioner',       label: 'Instruktioner',        category: 'stod',       iconName: 'BookOpen',
    alwaysVisible: true,
    tags: ['instruktion', 'instruktioner', 'hjälp', 'manual', 'guide', 'lathund', 'användarguide', 'så gör du'] },
  { to: '/meetings',            label: 'Möten',                category: 'workspace',  iconName: 'Calendar',
    moduleKey: 'meetings',
    tags: ['möten', 'kalender', 'mötesprotokoll', 'agenda'] },
  { to: '/issues',              label: 'Uppgifter',            category: 'workspace',  iconName: 'CheckSquare',
    moduleKey: 'issues',
    tags: ['uppgifter', 'todo', 'tasks', 'att göra'] },
  { to: '/kanai',               label: 'Fråga KanAI',          category: 'workspace',  iconName: 'Sparkles',
    moduleKey: 'kanai', ai: true,
    tags: ['ai', 'kanai', 'chatbot', 'hjälp', 'assistent', 'knowledge'] },

  // ─── Projekt ──────────────────────────────────────────────────────
  { to: '/projects',            label: 'Projekt',              category: 'projects',   iconName: 'FolderKanban',
    moduleKey: 'projects',
    tags: ['projekt', 'produktion', 'bygge', 'uppdrag'] },
  { to: '/bim',                 label: 'BIM',                  category: 'projects',   iconName: 'Box',
    moduleKey: 'bim_viewer',
    tags: ['bim', '3d', 'modell', 'ritning', 'projektering'] },
  { to: '/project-map',         label: 'Projektkarta',         category: 'projects',   iconName: 'Map',
    moduleKey: 'project_map',
    tags: ['karta', 'projekt', 'geografi', 'översikt'] },
  { to: '/reference-projects',  label: 'Referensprojekt',      category: 'projects',   iconName: 'FileImage',
    moduleKey: 'reference_projects',
    tags: ['referens', 'projekt', 'historik', 'cv', 'bilaga'] },
  { to: '/acc',                 label: 'ACC / BIM 360',        category: 'projects',   iconName: 'Building2',
    moduleKey: 'acc',
    tags: ['acc', 'bim360', 'autodesk', 'ritning', 'projekt'] },
  { to: '/spatial',             label: 'Spatial AI',           category: 'projects',   iconName: 'Sparkles',
    moduleKey: 'spatial', ai: true,
    tags: ['spatial', 'ai', 'rumslig', 'projekt'] },
  { to: '/historikarkiv',       label: 'Historikarkiv',        category: 'projects',   iconName: 'Archive',
    moduleKey: 'historikarkiv',
    tags: ['arkiv', 'historik', 'byggsamordnaren', 'bso', 'import', 'gamla projekt', 'archive'] },
  { to: '/smartdok-arkiv',      label: 'Smartdok-arkiv',       category: 'projects',   iconName: 'Archive',
    moduleKey: 'smartdok_archive',
    tags: ['smartdok', 'arkiv', 'historik', 'formulär', 'egenkontroller', 'projekt', 'bilder'] },
  { to: '/bildbank',           label: 'Bildbank',             category: 'projects',   iconName: 'Images',
    moduleKey: 'bildbank',
    tags: ['bildbank', 'bilder', 'foton', 'heic', 'nedladdning', 'företagsbilder', 'projektbilder', 'kanai'] },
  // Externa resurser P1 (default_on=False, Mikael F44): arbetar per projekt i projektfliken
  // Resurser och har ingen egen rutt. Med aktivt projekt öppnas dess flik (projectTab, F45),
  // annars projektlistan.
  { to: '/projects',            label: 'Inhyrda och maskiner', category: 'projects',   iconName: 'HardHat',
    moduleKey: 'externa_resurser', projectTab: 'resurser',
    tags: ['inhyrda', 'externa', 'resurser', 'maskiner', 'ue', 'underentreprenör', 'tid', 'attest', 'id06', 'extern tid'] },
  { to: '/portfoljrisk',        label: 'Risk & möjlighet',     category: 'management', iconName: 'TrendingUp',
    moduleKey: 'portfoljrisk',
    tags: ['risk', 'möjlighet', 'portfölj', 'portfoljrisk', 'pert', 'affärsområde', 'affärsrisk', 'projektrisk', 'styrelse', 'snapshot', 'kanai'] },
  { to: '/tidplan/framdrift',   label: 'Framdrift',            category: 'management', iconName: 'GanttChart',
    moduleKey: 'schedule_portfolio',
    tags: ['framdrift', 'tidplan', 'portfölj', 'kalkyltidplan', 'förskjutning', 'aktivitetstyp', 'kalkyl', 'utfall'] },

  // ─── Ledningssystem ───────────────────────────────────────────────
  { to: '/management-system',   label: 'Ledningssystem',       category: 'management', iconName: 'ListTodo',
    moduleKey: 'management_system',
    tags: ['ledningssystem', 'iso', '9001', '14001', '45001', 'kvalitet', 'miljö', 'arbetsmiljö'] },
  // Egna moduler efter Ledningssystemet (placering 2026-10, F18). Gamla adresser
  // under /management-system/ omdirigeras i App.js.
  { to: '/avvikelser',          label: 'Avvikelser & förbättring', category: 'management', iconName: 'AlertTriangle',
    moduleKey: 'avvikelser',
    tags: ['avvikelser', 'avvikelse', 'kvalitetsavvikelse', 'observation', 'förbättringsförslag', 'förbättring', 'rotorsak', 'åtgärd', 'reklamation', 'iso', '9001', '10.2'] },
  { to: '/mal-kpi',             label: 'Mål & nyckeltal',      category: 'management', iconName: 'Target',
    moduleKey: 'mal_kpi',
    tags: ['mål', 'nyckeltal', 'kpi', 'måluppfyllelse', 'scorecard', 'trend', 'iso', '6.2'] },
  { to: '/lagbevakning',        label: 'Lagbevakning',         category: 'management', iconName: 'Scale',
    moduleKey: 'lagbevakning',
    tags: ['lagbevakning', 'lagkrav', 'lag', 'lagar', 'författning', 'ramböll', 'efterlevnad', 'lagrevision', 'iso', 'afs', 'sfs', 'miljöbalken'] },
  { to: '/regelverk',           label: 'Regelverk',            category: 'management', iconName: 'BookOpen',
    moduleKey: 'regelverk',
    tags: ['regelverk', 'ama', 'mer', 'ama anläggning', 'ebr', 'esa', 'svenska kraftnät', 'trafikverket', 'tdok', 'svenskt vatten', 'teknisk handbok', 'standard', 'kanai'] },
  { to: '/grupper',             label: 'Grupper',              category: 'management', iconName: 'MessagesSquare',
    moduleKey: 'grupper',
    tags: ['grupper', 'grupp', 'samarbete', 'beslut', 'beslutslogg', 'innovationsgruppen', 'chatt', 'till alla'] },
  { to: '/larande',             label: 'Lärande',              category: 'management', iconName: 'Lightbulb',
    moduleKey: 'larande',
    tags: ['lärande', 'lärdomar', 'erfarenhet', 'erfarenhetsåterföring', 'förbättringsförslag', 'undvik', 'bra lösning'] },
  { to: '/rutinhantering',      label: 'Rutinhantering',       category: 'management', iconName: 'FileCheck',
    moduleKey: 'routine_management',
    tags: ['rutiner', 'dokument', 'policy', 'instruktion', 'mallar', 'rutin'] },
  { to: '/revisions',           label: 'Revision',             category: 'management', iconName: 'ClipboardList',
    moduleKey: 'revisions',
    tags: ['revision', 'internrevision', 'audit', 'iso', 'kvalitet', 'granskning'] },
  { to: '/hms',                 label: 'HMS',                  category: 'management', iconName: 'ShieldAlert',
    moduleKey: 'ia_system',
    tags: ['hms', 'hälsa', 'miljö', 'säkerhet', 'arbetsmiljö', 'avvikelser', 'incident', 'tillbud',
           'olycka', 'riskobservation', 'riskobs', 'risk', 'skyddsrond', 'sja', 'skada', 'kvalitet',
           'ia', 'ia-systemet'] },
  { to: '/skyddsronder',        label: 'Skyddsronder',         category: 'management', iconName: 'ShieldCheck',
    moduleKey: 'ia_system',
    tags: ['skyddsrond', 'arbetsmiljö', 'säkerhet', 'hms', 'ams', 'rond'] },
  { to: '/arbetsmiljodelegering', label: 'Arbetsmiljödelegeringar', category: 'management', iconName: 'Network',
    moduleKey: 'arbetsmiljodelegering',
    tags: ['arbetsmiljödelegering', 'delegering', 'arbetsmiljöansvar', 'uppgiftsfördelning', 'sam', 'afs 2023:1',
           'aml', 'platschef', 'projektchef', 'affärsområdeschef', 'hms'] },
  { to: '/qa/controls',         label: 'QA Kontrollplan',      category: 'management', iconName: 'ClipboardCheck',
    moduleKey: 'qa_controls',
    tags: ['qa', 'kontroll', 'kvalitet', 'egenkontroll', 'kontrollplan'] },
  { to: '/arbetsberedningar',   label: 'Arbetsberedningar',    category: 'management', iconName: 'AlertTriangle',
    moduleKey: 'arbetsberedning',
    tags: ['arbetsberedning', 'beredning', 'risk', 'riskhantering', 'moment', 'ab', 'afs'] },
  { to: '/ek-bibliotek',        label: 'EK-biblioteket',       category: 'management', iconName: 'BookOpen',
    moduleKey: 'ek_bibliotek',
    tags: ['ek', 'egenkontroll', 'bibliotek', 'kvalitet', 'mallar', 'kontroll'] },
  { to: '/klimat',              label: 'Klimatberäkning',      category: 'management', iconName: 'Leaf',
    moduleKey: 'klimat',
    tags: ['klimat', 'co2', 'miljö', 'epd', 'hållbarhet', 'koldioxid'] },
  { to: '/waste-analysis',      label: 'Avfallsanalys',        category: 'management', iconName: 'Trash2',
    moduleKey: 'waste_analysis',
    tags: ['avfall', 'miljö', 'hållbarhet', 'sortering', 'återvinning'] },
  { to: '/energy',              label: 'Energi & Utsläpp',     category: 'management', iconName: 'Zap',
    moduleKey: 'energy',
    tags: [
      'energi', 'utslapp', 'utsläpp', 'co2', 'co2e', 'koldioxid', 'klimat', 'ghg', 'hållbarhet',
      'scope 1', 'scope 2', 'scope1', 'scope2',
      'drivmedel', 'diesel', 'bensin', 'hvo', 'hvo100', 'etanol', 'fleet', 'fordon',
      'el', 'elförbrukning', 'kwh', 'elnät', 'el-mix', 'svensk mix', 'ursprungsgaranti',
      'gas', 'biogas', 'naturgas', 'fordonsgas', 'lpg', 'gasol',
      'energimyndigheten', 'co2-faktor', 'koncernrapport',
      'cirkel k', 'circle k', 'okq8', 'skogsbränsle', 'tranås energi',
    ] },
  { to: '/insight',             label: 'Regler & Nyheter',     category: 'management', iconName: 'Newspaper',
    moduleKey: 'insight',
    tags: ['regler', 'lagkrav', 'knowledge', 'nyheter', 'kunskap', 'wiki'] },
  { to: '/forms',               label: 'Formulär',             category: 'management', iconName: 'FormInput',
    moduleKey: 'forms',
    tags: ['formulär', 'mall', 'inrapportering', 'enkät'] },
  // ─── Affär & Ekonomi ──────────────────────────────────────────────
  // Årshjul (BL-195): ingen menypost (Mikael F31), bara sökträff. Den går till Start, som
  // andra sökträffar byter sida, och panelen väljer första publicerade hjulet användaren ser.
  // adminOnly: före öppning syns träffen bara för systemadmin (uppdrag BL-195 punkt 3).
  // TA BORT adminOnly när Mikael öppnar modulen i rollerna, annars får de ingen sökträff.
  { to: '/home/overview?arshjul=aktuellt', label: 'Årshjul',              category: 'finance',    iconName: 'CalendarRange',
    moduleKey: 'arshjul', searchOnly: true, adminOnly: true,
    tags: ['årshjul', 'bokslut', 'deadline', 'tidplan', 'af-rapportering', 'interim', 'prognos'] },
  { to: '/finance-dashboard',   label: 'Ekonomidashboard',     category: 'finance',    iconName: 'LayoutDashboard',
    moduleKey: 'finance_dashboard',
    tags: ['ekonomi', 'dashboard', 'finans', 'pengar', 'resultat'] },
  { to: '/insight-analytics',   label: 'Insight Analytics',    category: 'finance',    iconName: 'LineChart',
    moduleKey: 'insight_analytics',
    tags: ['insight', 'analytics', 'analys', 'ekonomi', 'rapport', 'bi'] },
  { to: '/reports-360',         label: '360 Rapporter',        category: 'finance',    iconName: 'FileBarChart',
    moduleKey: 'reports_360',
    tags: ['rapporter', '360', 'ekonomi', 'månadsrapport'] },
  { to: '/management-dashboard', label: 'Ledningsdashboard',   category: 'finance',    iconName: 'BarChart3',
    moduleKey: 'management_dashboard',
    tags: ['ledning', 'dashboard', 'ekonomi', 'kpi', 'styrelse'] },
  { to: '/executive-dashboard', label: 'Executive Dashboard',  category: 'finance',    iconName: 'BarChart',
    moduleKey: 'executive_dashboard',
    tags: ['executive', 'dashboard', 'vd', 'ledning', 'ekonomi'] },
  { to: '/kancalc',             label: 'KanCalc',              category: 'finance',    iconName: 'Calculator',
    moduleKey: 'kancalc',
    tags: ['kalkyl', 'anbud', 'kostnad', 'ekonomi', 'pris'] },
  { to: '/mangdning',           label: 'Mängdning',            category: 'finance',    iconName: 'Ruler',
    moduleKey: 'mangdning',
    tags: ['mängdning', 'mängder', 'ritning', 'kalkyl', 'ama', 'va', 'teckenförklaring', 'mätning'] },
  { to: '/calcreview',          label: 'Kalkylgranskning',     category: 'finance',    iconName: 'Search',
    moduleKey: 'calc_review',
    tags: ['kalkyl', 'granskning', 'ekonomi', 'review'] },
  { to: '/tenders',             label: 'Anbud',                category: 'finance',    iconName: 'TrendingUp',
    moduleKey: 'tenders',
    tags: ['anbud', 'tender', 'offert', 'affär', 'försäljning', 'kund', 'ekonomi'] },
  { to: '/anbud-v2',            label: 'Anbud V2',             category: 'finance',    iconName: 'LayoutGrid',
    moduleKey: 'tenders_v2',
    tags: ['anbud', 'anbud v2', 'tavla', 'genomgång', 'crm', 'kund', 'pilot'] },
  { to: '/contracts',           label: 'Avtal',                category: 'finance',    iconName: 'FileText',
    moduleKey: 'contracts',
    tags: ['avtal', 'kontrakt', 'juridik', 'kund', 'leverantör'] },
  { to: '/avtal-byggaren',      label: 'Avtalsbyggaren',       category: 'finance',    iconName: 'PenLine',
    moduleKey: 'agreements',
    tags: ['avtal', 'mall', 'signering', 'scrive', 'byggare', 'redigera', 'kanai'] },
  { to: '/suppliers',           label: 'Leverantörer',         category: 'finance',    iconName: 'Truck',
    moduleKey: 'supplier_api',
    tags: ['leverantörer', 'ue', 'inköp', 'ekonomi', 'avtal'] },
  { to: '/purchasing',          label: 'Inköp',                category: 'finance',    iconName: 'ShoppingCart',
    moduleKey: 'purchasing',
    tags: ['inköp', 'beställning', 'leverantör', 'ekonomi'] },
  { to: '/fakturagranskning',   label: 'Fakturagranskning',    category: 'finance',    iconName: 'FileWarning',
    moduleKey: 'invoice_review',
    tags: ['faktura', 'granskning', 'leverantör', 'kontroll', 'projekt', 'ekonomi', 'attest'] },
  { to: '/ue-lista',            label: 'UE-lista',             category: 'finance',    iconName: 'ClipboardCheck',
    moduleKey: 'ue_lista',
    tags: ['ue', 'underentreprenör', 'leverantör', 'lista'] },
  { to: '/foretagsinskrivning', label: 'Företagsinskrivning',  category: 'finance',    iconName: 'Building',
    moduleKey: 'foretagsinskrivning',
    tags: ['företagsinskrivning', 'ue', 'underentreprenör', 'klartecken', 'afs', 'bas-u', 'bas-p', 'kravdokument', 'maskinförteckning', 'riskbedömning', 'arbetsmiljöplan', 'inskrivning'] },
  { to: '/projects',            label: 'Inskrivning',          category: 'projects',   iconName: 'UserCheck',
    moduleKey: 'p.inskrivningar',
    tags: ['inskrivning', 'qr', 'id06', 'arbetare', 'tilltrade', 'bodgrind', 'safe construction training', 'sct', 'sakerhetsutbildning', 'policygodkannande', 'signatur', 'kvitto', 'underentreprenör', 'ue', 'kanonaden', 'bas-u', 'projektledning'] },
  { to: '/open-books',          label: 'Open Books',           category: 'finance',    iconName: 'BookOpen',
    moduleKey: 'open_books',
    tags: ['openbooks', 'öppen bok', 'ekonomi', 'transparens', 'samverkan', 'partnering'] },
  { to: '/inventarier',         label: 'Inventarier',          category: 'finance',    iconName: 'Box',
    moduleKey: 'inventarier',
    tags: ['inventarier', 'anläggningstillgångar', 'avskrivning', 'maskiner', 'fordon', 'hogia', 'ekonomi'] },
  // Personligt systemadmin-verktyg (beslut Mikael 2026-10-01): ingen modulnyckel,
  // syns bara för rollen systemadmin (systemAdminOnly, se utils/systemadminVy.js).
  { to: '/etf-allocation',      label: 'ETF Allocation',       category: 'finance',    iconName: 'PieChart',
    adminOnly: true, systemAdminOnly: true,
    tags: ['etf', 'investering', 'portfölj', 'aktier'] },
  { to: '/crm',                 label: 'CRM',                  category: 'finance',    iconName: 'UserCheck',
    moduleKey: 'crm',
    tags: ['crm', 'kund', 'försäljning', 'kontakt', 'pipeline'] },
  { to: '/vaxel',               label: 'Växel',                category: 'finance',    iconName: 'Phone',
    moduleKey: 'vaxel',
    tags: ['växel', 'voicemail', 'telefon', 'rostmeddelande', 'samtal', 'inkommande', '46elks', 'kommunikation'] },
  { to: '/eftermarknad',        label: 'Eftermarknad',         category: 'finance',    iconName: 'Wrench',
    moduleKey: 'eftermarknad',
    tags: ['eftermarknad', 'service', 'underhåll', 'garanti'] },

  // ─── Verktyg & Personal ───────────────────────────────────────────
  { to: '/contacts',            label: 'Kontakter',            category: 'tools',      iconName: 'Users',
    moduleKey: 'contacts',
    tags: ['kontakter', 'adressbok', 'personal', 'crm'] },
  { to: '/orgplan',             label: 'Projektorganisation',  category: 'tools',      iconName: 'Network',
    moduleKey: 'orgplan',
    tags: ['organisation', 'organisationsplan', 'projektorg', 'orgplan', 'hierarki', 'personal'] },
  { to: '/hrm',                 label: 'HR Management',        category: 'tools',      iconName: 'UserCog',
    moduleKey: 'hrm',
    tags: ['hr', 'personal', 'management', 'anställda'] },
  { to: '/hr/utbildning',       label: 'Utbildning och kompetens', category: 'tools', iconName: 'GraduationCap',
    moduleKey: 'hrm_kansliga',
    tags: ['utbildning', 'kompetens', 'intyg', 'certifikat', 'smartdok', 'import', 'hr'] },
  { to: '/kanprofil',           label: 'KanProfil',            category: 'tools',      iconName: 'Compass',
    moduleKey: 'kanprofil',
    tags: ['kanprofil', 'profil', 'personlighet', 'team', 'samspel', 'driv', 'struktur', 'nytänk', 'lag', 'utveckling', 'självkännedom'] },
  { to: '/kanprofil/laganalys', label: 'KanProfil — Laganalys', category: 'tools',     iconName: 'Users',
    moduleKey: 'kanprofil',
    tags: ['laganalys', 'team', 'lag', 'kanprofil', 'profil', 'projektteam', 'sammansättning', 'kravprofil', 'kanai'] },
  { to: '/assessments',         label: 'Medarbetarsamtal',     category: 'tools',      iconName: 'MessagesSquare',
    moduleKey: 'assessments',
    tags: ['medarbetarsamtal', 'hr', 'samtal', 'utveckling', 'personal'] },
  { to: '/leadership-assessment', label: 'Ledarskapsbedömning', category: 'tools',     iconName: 'Star',
    moduleKey: 'leadership_assessment',
    tags: ['ledarskap', 'bedömning', 'hr', 'personal', 'chef'] },
  { to: '/training',            label: 'Utbildning',           category: 'tools',      iconName: 'GraduationCap',
    moduleKey: 'onboarding',
    tags: ['utbildning', 'kurs', 'certifikat', 'kompetens', 'personal', 'kanakademin'] },
  { to: '/time-reporting',      label: 'Tidsredovisning',      category: 'tools',      iconName: 'Clock',
    moduleKey: 'time_reporting',
    tags: ['tid', 'timmar', 'rapport', 'personal', 'lön'] },
  { to: '/utlagg',              label: 'Utlägg',               category: 'tools',      iconName: 'Receipt',
    moduleKey: 'utlagg',
    tags: ['utlägg', 'kvitto', 'kostnad', 'ersättning', 'reseräkning'] },
  { to: '/ledighet',            label: 'Ledighet',             category: 'tools',      iconName: 'CalendarDays',
    moduleKey: 'ledighet',
    tags: ['ledighet', 'semester', 'komp', 'tjänstledighet', 'frånvaro', 'personal'] },
  { to: '/payroll',             label: 'Lönehantering',        category: 'tools',      iconName: 'Wallet',
    moduleKey: 'payroll',
    tags: ['lön', 'personal', 'ekonomi', 'hogia'] },
  { to: '/fleet',               label: 'Företagsbilar',        category: 'tools',      iconName: 'Car',
    moduleKey: 'fleet',
    tags: ['fordon', 'bilar', 'fleet', 'personal', 'förmån'] },
  { to: '/infobric-fleet',      label: 'Infobric Fleet',       category: 'tools',      iconName: 'Satellite',
    moduleKey: 'infobric_fleet',
    tags: ['infobric', 'telliq', 'fleet', 'fordon', 'gps', 'körjournal', 'positioner', 'bränsle', 'drivmedel', 'tracking'] },
  { to: '/cv-generator',        label: 'CV-Generator',         category: 'tools',      iconName: 'User',
    moduleKey: 'cv_generator',
    tags: ['cv', 'personal', 'kompetens', 'anbud', 'referens'] },
  { to: '/resource-planning',   label: 'Resurser',             category: 'tools',      iconName: 'Users',
    moduleKey: 'resource_planning',
    tags: ['resurser', 'maskiner', 'utrustning', 'personal'] },
  { to: '/signature-generator', label: 'Signaturgenerator',    category: 'tools',      iconName: 'PenTool',
    moduleKey: 'signature_generator',
    tags: ['signatur', 'e-post', 'mail', 'personal'] },
  { to: '/modules',             label: 'Modulbibliotek',       category: 'tools',      iconName: 'LayoutGrid',
    adminOnly: true,
    tags: ['moduler', 'bibliotek', 'översikt'] },
  // Modulnyckel sedan BL-167 (Mikael F74): slås på per organisation. Ersätter på sikt /changelog.
  { to: '/pa-gang',             label: 'På gång',              category: 'tools',      iconName: 'Activity',
    moduleKey: 'pa_gang',
    tags: ['på gång', 'backlog', 'planerat', 'levererat', 'ändringslogg', 'changelog', 'nyheter', 'tidplan'] },

  // ─── Administration (collapsed by default) ────────────────────────
  { to: '/admin',               label: 'Systemadmin',          category: 'admin',      iconName: 'Settings',
    adminOnly: true,
    tags: ['admin', 'system', 'inställningar', 'roller'] },
  { to: '/admin/tenants',       label: 'Företag',              category: 'admin',      iconName: 'Building',
    adminOnly: true,
    tags: ['företag', 'tenants', 'kunder', 'admin', 'saas'] },
  { to: '/module-analytics',    label: 'Modulstatistik',       category: 'admin',      iconName: 'BarChart3',
    adminOnly: true,
    tags: ['statistik', 'användning', 'admin', 'telemetri'] },
  { to: '/feedback-board',      label: 'Feedback Pipeline',    category: 'admin',      iconName: 'MessageSquare',
    adminOnly: true,
    tags: ['feedback', 'buggar', 'pipeline', 'admin'] },
  { to: '/tenant-admin',        label: 'Företagsadmin',        category: 'admin',      iconName: 'Building',
    tenantAdminOnly: true,
    tags: ['företag', 'admin', 'inställningar', 'tenant'] },
];

// Regeln för vem som har Anbud V2 som enda anbudsingång, även admin
// (BL-199, Mikael F5). Menyer, sök och omledningen läser härifrån.
export function harAnbudV2(moduler) {
  return Boolean(moduler?.has?.('tenders_v2') && moduler?.has?.('tenders'));
}

export function ersattAvAnbudV2(modul, moduler) {
  return modul?.to === '/tenders' && harAnbudV2(moduler);
}

export const MODULES_BY_NAME = Object.fromEntries(
  MODULES.map((m) => [m.label, m]),
);

export const MODULES_BY_ROUTE = Object.fromEntries(
  MODULES.map((m) => [m.to, m]),
);

// Default favorites for users with no saved preference. Picked to surface
// high-frequency daily tasks across roles.
export const DEFAULT_FAVORITES = [
  'Uppgifter',
  'Skyddsronder',
  'Tidsredovisning',
];

// Variant migration map (one-time): legacy users had a 3-variant menu
// preference. When the new sidebar mounts and finds no favorites, we use
// their old variant choice to seed favorites that match what they were
// used to seeing prominently. Removed from code once telemetry shows
// nobody is on legacy variants anymore.
export const VARIANT_MIGRATION_FAVORITES = {
  A: ['Uppgifter', 'Skyddsronder', 'Tidsredovisning', 'Medarbetarsamtal'],
  B: ['KanCalc', 'Anbud', 'Kalkylgranskning', 'Inköp', 'Insight Analytics'],
  C: ['Uppgifter'],
};
